import React from 'react'
import {
  Text,
  Heading,
  Flex,
  Box,
  Divider,
  AlertBanner,
  AlertBannerText,
  LinearProgressBar,
} from '@vibe/core'
import type { Candidate } from '../../types'

interface AIEvaluationProps {
  candidate: Candidate
}

/**
 * Component for displaying AI evaluation results with detailed scoring and reasoning
 */
export const AIEvaluation: React.FC<AIEvaluationProps> = ({ candidate }) => {
  const getEvaluationStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completada'
      case 'processing':
        return 'Procesando'
      case 'error':
        return 'Error'
      case 'pending':
        return 'Pendiente'
      default:
        return status
    }
  }

  // Check if AI evaluation data exists in the real data structure
  const hasEvaluationData =
    candidate.evaluacion_ai && candidate.evaluacion_ai.length > 0
  const aiStatus = candidate.estado_ai || 'pending'
  const matchScore = candidate.match || 0

  if (!hasEvaluationData) {
    return (
      <Box className="p-6">
        <AlertBanner backgroundColor="warning">
          <AlertBannerText text="La evaluación de IA no está disponible para este candidato" />
        </AlertBanner>
      </Box>
    )
  }

  if (aiStatus === 'error') {
    return (
      <Box className="p-6">
        <AlertBanner backgroundColor="negative">
          <AlertBannerText text="Error en la evaluación de IA. Por favor, intente nuevamente." />
        </AlertBanner>
      </Box>
    )
  }

  if (aiStatus === 'procesando') {
    return (
      <Box className="p-6">
        <AlertBanner backgroundColor="primary">
          <AlertBannerText text="La evaluación de IA está en proceso. Los resultados estarán disponibles pronto." />
        </AlertBanner>
      </Box>
    )
  }

  if (aiStatus === 'pendiente') {
    return (
      <Box className="p-6">
        <AlertBanner backgroundColor="primary">
          <AlertBannerText text="La evaluación de IA está pendiente de procesamiento." />
        </AlertBanner>
      </Box>
    )
  }

  // Calculate total points from evaluation items
  const totalPossiblePoints =
    candidate.evaluacion_ai?.reduce((sum, item) => sum + item.total, 0) || 0
  const totalObtainedPoints =
    candidate.evaluacion_ai?.reduce((sum, item) => sum + item.puntaje, 0) || 0
  const overallPercentage =
    totalPossiblePoints > 0
      ? Math.round((totalObtainedPoints / totalPossiblePoints) * 100)
      : matchScore

  return (
    <div className="space-y-6">
      {/* Overall Score Section */}
      <Box className="mb-6">
        <Heading type="h3" className="mb-4">
          Resumen de Evaluación
        </Heading>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Flex justify="space-between" align="center" className="mb-3">
              <Text>Puntuación Total:</Text>
              <Text weight="bold">{totalObtainedPoints}%</Text>
            </Flex>

            <LinearProgressBar
              value={overallPercentage}
              max={100}
              className="w-full mb-3"
            />
          </div>

          <div className="space-y-3">
            <div>
              <Text type="text3" color="secondary" className="mb-1">
                Estado de Evaluación
              </Text>
              <Text>{getEvaluationStatusText(aiStatus)}</Text>
            </div>

            {candidate.last_updated && (
              <div>
                <Text type="text3" color="secondary" className="mb-1">
                  Fecha de Evaluación
                </Text>
                <Text>
                  {new Date(candidate.last_updated).toLocaleDateString(
                    'es-ES',
                    {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  )}
                </Text>
              </div>
            )}
          </div>
        </div>
      </Box>

      <Divider />

      {/* Detailed Evaluation Section */}
      <Box className="mb-6">
        <Heading type="h3" className="mb-4">
          Evaluación Detallada por Criterio
        </Heading>

        <div className="space-y-4">
          {candidate.evaluacion_ai?.map((item, index) => {
            const percentage = Math.round((item.puntaje / item.total) * 100)

            return (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start gap-4 mb-3">
                  <h4 className="flex-1 text-lg font-semibold text-gray-900 leading-tight min-w-0">
                    {item.requisito}
                  </h4>
                  <Text weight="medium" className="flex-shrink-0">
                    {item.puntaje}% de {item.total}%
                  </Text>
                </div>

                <LinearProgressBar
                  value={percentage}
                  max={100}
                  className="w-full mb-3"
                />

                <Box className="bg-gray-50 rounded-md p-3">
                  <Text type="text3" color="secondary" className="mb-1">
                    Justificación:
                  </Text>
                  <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap break-words overflow-visible">
                    {item.motivo}
                  </div>
                </Box>
              </div>
            )
          })}
        </div>
      </Box>

      {/* Additional Information Section */}
      {candidate.extracted_data && (
        <>
          <Divider />
          <Box className="mb-6">
            <Heading type="h3" className="mb-4">
              Información Extraída del CV
            </Heading>
            <Box className="bg-blue-50 rounded-lg p-4 space-y-2">
              {candidate.extracted_data.anos_experiencia && (
                <div>
                  <Text type="text3" color="secondary">
                    Años de experiencia:{' '}
                  </Text>
                  <Text weight="medium">
                    {candidate.extracted_data.anos_experiencia} años
                  </Text>
                </div>
              )}
              {candidate.extracted_data.cargo_actual && (
                <div>
                  <Text type="text3" color="secondary">
                    Cargo actual:{' '}
                  </Text>
                  <Text weight="medium">
                    {candidate.extracted_data.cargo_actual}
                  </Text>
                </div>
              )}
            </Box>
          </Box>
        </>
      )}
    </div>
  )
}
